import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useNavigate } from "react-router-dom";

import { Button, LinkButton } from "../../../components/Button";
import { LoadingModal } from "../../../components/LoadingModal";
import { Box, Container as BarContainer, StickySpacer } from "../../../components/SaveBar/styles";
import { ScreenHeader } from "../../../components/SubHeader";
import {
  AccountDeletionSurvey,
  accountDeletionService,
  DeletionReason,
  DeletionStep,
} from "../../../services/accountDeletion";
import { api, ApiSiteResponse, hasActiveSubscription } from "../../../services/api";
import { isApiError } from "../../../services/api/errors";
import { signOut } from "../../../services/auth";
import { uuidv4 } from "../../../utils/uuid";
import {
  Container,
  Field,
  Losses,
  Note,
  OfferCard,
  Option,
  Options,
  SecondaryButton,
  Step,
} from "../../Subscription/pages/Cancel/styles";

const prefix = "pages.accountDelete";

const REASONS: DeletionReason[] = [
  "notUsing",
  "justTesting",
  "competitor",
  "hardToUse",
  "missingFeature",
  "technical",
  "privacy",
  "duplicate",
];

/** The quick question some reasons get before the free text (texts under `pages.accountDelete.detail.{reason}`). */
const FOLLOW_UPS: Partial<Record<DeletionReason, { multiple: boolean; options: string[] }>> = {
  competitor: { multiple: false, options: ["linktree", "beacons", "ownSite", "instagram", "other"] },
  missingFeature: {
    multiple: true,
    options: ["store", "scheduling", "integrations", "analytics", "design", "other"],
  },
};

/** Reasons where talking to the support may solve it: the detail step offers it. */
const SUPPORT_REASONS: DeletionReason[] = ["hardToUse", "technical"];

// Shuffled per user (so the first option isn't picked for being first), "other" always last
const shuffle = (reasons: DeletionReason[]) => {
  const list = [...reasons];
  for (let i = list.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [list[i], list[j]] = [list[j], list[i]];
  }
  return [...list, "other" as const];
};

type FlowStep = Exclude<DeletionStep, "deleted">;

/**
 * Asks why the user is deleting their account (reason, an optional detail), deletes it after they
 * type the confirmation phrase and signs them out. Every step is saved. Accounts with a PLUS subscription still charging
 * don't get here: /account asks to cancel it first (and the backend refuses too).
 */
const DeleteFlow = ({ sites }: { sites: ApiSiteResponse[] }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [surveyId] = useState(uuidv4);
  const [reasons] = useState(() => shuffle(REASONS));
  const [step, setStep] = useState<FlowStep>("reason");

  const [reason, setReason] = useState<DeletionReason>();
  const [reasonText, setReasonText] = useState("");
  const [detailOptions, setDetailOptions] = useState<string[]>([]);
  const [detailText, setDetailText] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [deleteState, setDeleteState] = useState<"idle" | "deleting" | "error" | "plus">("idle");

  const keeposCount = sites.filter(({ site, draft }) => !!(site || draft)).length;
  const followUp = reason ? FOLLOW_UPS[reason] : undefined;

  const save = useCallback(
    (reached: DeletionStep) => {
      const survey: AccountDeletionSurvey = {
        surveyId,
        step: reached,
        reason,
        reasonText: reason === "other" ? reasonText.trim() || undefined : undefined,
        detail: {
          options: detailOptions.length ? detailOptions : undefined,
          text: detailText.trim() || undefined,
        },
        context: {
          keeposCount,
          hadPlus: sites.some(({ subscription }) => subscription?.type === "PLUS"),
          locale: i18n.language,
        },
      };
      return accountDeletionService.saveProgress(survey).catch(() => undefined);
    },
    [surveyId, reason, reasonText, detailOptions, detailText, keeposCount, sites, i18n.language]
  );

  // Starting the flow is data too (how many start and don't delete)
  useEffect(() => {
    save("reason");
  }, []);

  const goTo = (next: FlowStep) => {
    save(next);
    setStep(next);
    window.scrollTo({ top: 0 });
  };

  const phrase = t(`${prefix}.confirm.phrase`);
  const isConfirmed = confirmation.trim().toLowerCase() === phrase.toLowerCase();

  const deleteAccount = async () => {
    setDeleteState("deleting");
    try {
      await accountDeletionService.deleteAccount();
      // The account is gone (the rest runs in the background): record it and sign out right away
      await save("deleted");
      await signOut();
    } catch (e) {
      setDeleteState(isApiError(e, "PLUS_SUBSCRIPTION_ACTIVE") ? "plus" : "error");
    }
  };

  const toggleOption = (option: string, multiple: boolean) =>
    setDetailOptions(current =>
      !multiple ? [option] : current.includes(option) ? current.filter(item => item !== option) : [...current, option]
    );

  const canContinue =
    step === "reason"
      ? !!reason && (reason !== "other" || !!reasonText.trim())
      : step === "confirm"
        ? isConfirmed
        : true;

  const steps: FlowStep[] = ["reason", "detail", "confirm"];
  const index = steps.indexOf(step);

  return (
    <>
      <Step>
        <div className="progress" aria-live="polite">
          {t(`${prefix}.progress`, { current: index + 1, total: steps.length })}
          <div className="bar" aria-hidden="true">
            <span style={{ width: `${((index + 1) / steps.length) * 100}%` }} />
          </div>
        </div>

        {step === "reason" && (
          <>
            <h3>{t(`${prefix}.reason.title`)}</h3>
            <p className="description">{t(`${prefix}.reason.description`)}</p>
            <Options role="radiogroup">
              {reasons.map(key => (
                <Option
                  key={key}
                  type="button"
                  role="radio"
                  aria-checked={reason === key}
                  $selected={reason === key}
                  onClick={() => {
                    setReason(key);
                    setDetailOptions([]);
                  }}>
                  <span className="mark" />
                  {t(`${prefix}.reason.options.${key}`)}
                </Option>
              ))}
            </Options>
            {reason === "other" && (
              <Field>
                <textarea
                  value={reasonText}
                  maxLength={1000}
                  placeholder={t(`${prefix}.reason.otherPlaceholder`)}
                  onChange={event => setReasonText(event.target.value)}
                />
              </Field>
            )}
          </>
        )}

        {step === "detail" && reason && (
          <>
            {followUp && (
              <>
                <h3>{t(`${prefix}.detail.${reason}.title`)}</h3>
                <Options role={followUp.multiple ? "group" : "radiogroup"}>
                  {followUp.options.map(key => (
                    <Option
                      key={key}
                      type="button"
                      role={followUp.multiple ? "checkbox" : "radio"}
                      aria-checked={detailOptions.includes(key)}
                      $selected={detailOptions.includes(key)}
                      $multiple={followUp.multiple}
                      onClick={() => toggleOption(key, followUp.multiple)}>
                      <span className="mark" />
                      {t(`${prefix}.detail.${reason}.options.${key}`)}
                    </Option>
                  ))}
                </Options>
              </>
            )}
            {!followUp && <h3>{t(`${prefix}.detail.title`)}</h3>}
            {SUPPORT_REASONS.includes(reason) && (
              <OfferCard>
                <h4>{t(`${prefix}.detail.support.title`)}</h4>
                <p>{t(`${prefix}.detail.support.description`)}</p>
                {/* Help is a main screen (no back button): tell it to come back here */}
                <SecondaryButton
                  type="button"
                  onClick={() =>
                    navigate("/help", {
                      state: { back: { label: t(`${prefix}.header.title`), to: "/account/delete" } },
                    })
                  }>
                  {t(`${prefix}.detail.support.cta`)}
                </SecondaryButton>
              </OfferCard>
            )}
            <Field>
              {t(`${prefix}.detail.textLabel`)}
              <textarea
                value={detailText}
                maxLength={1000}
                placeholder={t(`${prefix}.detail.textPlaceholder`)}
                onChange={event => setDetailText(event.target.value)}
              />
            </Field>
          </>
        )}

        {step === "confirm" && (
          <>
            <h3>{t(`${prefix}.confirm.title`)}</h3>
            <p className="description">{t(`${prefix}.confirm.description`)}</p>
            <Losses>
              <li>{t(`${prefix}.confirm.losses.keepos`, { count: keeposCount })}</li>
              <li>{t(`${prefix}.confirm.losses.addresses`)}</li>
              <li>{t(`${prefix}.confirm.losses.data`)}</li>
              <li>{t(`${prefix}.confirm.losses.login`)}</li>
            </Losses>
            <Field>
              {t(`${prefix}.confirm.typeLabel`, { phrase })}
              <input
                id="delete-confirmation"
                type="text"
                autoComplete="off"
                value={confirmation}
                placeholder={phrase}
                onChange={event => setConfirmation(event.target.value)}
              />
            </Field>
            {deleteState === "error" && <Note className="error">{t(`${prefix}.confirm.error`)}</Note>}
            {deleteState === "plus" && <Note className="error">{t(`${prefix}.confirm.plusError`)}</Note>}
          </>
        )}
      </Step>

      {/* Same fixed bar as the editor's save bar */}
      <StickySpacer />
      <Box $sticky>
        <BarContainer>
          <LinkButton onClick={() => (index > 0 ? setStep(steps[index - 1]) : navigate("/account"))}>
            {t(`${prefix}.back`)}
          </LinkButton>
          {step === "confirm" ? (
            <Button
              type="DANGER"
              size="LG"
              disabled={!isConfirmed || deleteState === "deleting"}
              onClick={() => isConfirmed && deleteState !== "deleting" && deleteAccount()}>
              {deleteState === "deleting" ? t(`${prefix}.confirm.deleting`) : t(`${prefix}.confirm.cta`)}
            </Button>
          ) : (
            <Button type="INFO" size="LG" disabled={!canContinue} onClick={() => canContinue && goTo(steps[index + 1])}>
              {t(`${prefix}.next`)}
            </Button>
          )}
        </BarContainer>
      </Box>
    </>
  );
};

/** `/account/delete`: the account deletion flow (from the "Excluir conta" block in /account). */
export const AccountDeletePage = () => {
  const { t } = useTranslation();
  const [sites, setSites] = useState<ApiSiteResponse[]>();

  useEffect(() => {
    api
      .getSites()
      .then(setSites)
      .catch(() => setSites([]));
  }, []);

  // A PLUS subscription still charging has to be cancelled first (the modal in /account explains it)
  const hasPlusCharging = useMemo(
    () => !!sites?.some(({ subscription }) => hasActiveSubscription(subscription) && !subscription?.canceled),
    [sites]
  );

  if (!sites) {
    return <LoadingModal />;
  }
  if (hasPlusCharging) {
    return <Navigate to="/account" replace />;
  }

  return (
    <Container>
      <ScreenHeader
        title={t(`${prefix}.header.title`)}
        back={{ label: t("pages.account.header.title"), to: "/account" }}
      />
      <DeleteFlow sites={sites} />
    </Container>
  );
};
